import React, { useState } from "react";

interface FilterComponentProps {
  onFilterChange?: (filters: FilterState) => void;
}

interface FilterState {
  gender: string[];
  age: string[];
  niche: string[];
  situation: string[];
  location: string[];
}

export default function FilterComponent({
  onFilterChange,
}: FilterComponentProps) {
  const [filters, setFilters] = useState<FilterState>({
    gender: [],
    age: [],
    niche: [],
    situation: [],
    location: [],
  });

  const handleFilterChange = (category: keyof FilterState, value: string) => {
    const updatedFilters = { ...filters };

    if (updatedFilters[category].includes(value)) {
      // Remove the value if it's already selected
      updatedFilters[category] = updatedFilters[category].filter(
        (item) => item !== value
      );
    } else {
      // Add the value if it's not already selected
      updatedFilters[category] = [...updatedFilters[category], value];
    }

    setFilters(updatedFilters);

    if (onFilterChange) {
      onFilterChange(updatedFilters);
    }
  };

  const isSelected = (category: keyof FilterState, value: string) => {
    return filters[category].includes(value);
  };

  return (
    <div className="filter-container p-6">
      <h2 className="text-2xl font-bold mb-4">Filter</h2>

      {/* All Videos and Starred section */}
      <div className="mb-6">
        <div className="filter-option bg-blue-50 p-3 rounded-md mb-2 flex items-center">
          <span className="material-icons mr-2 text-blue-500">videocam</span>
          <span className="font-medium">All Videos</span>
        </div>
        <div className="filter-option p-3 rounded-md flex items-center">
          <span className="material-icons mr-2 text-gray-400">star</span>
          <span className="font-medium text-gray-600">Starred</span>
        </div>
      </div>

      {/* Search section */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-2">Search</h3>
        <div className="relative">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              ></path>
            </svg>
          </span>
          <input
            type="text"
            className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Search actor by name..."
          />
        </div>
      </div>

      {/* Tags section */}
      <div>
        <h3 className="text-lg font-medium mb-2">Tags</h3>

        {/* Gender */}
        <div className="mb-4">
          <h4 className="text-sm text-gray-500 mb-2">Gender</h4>
          <div className="flex flex-wrap gap-2">
            {["Male", "Female"].map((gender) => (
              <button
                key={gender}
                className={`px-3 py-1 rounded-md border ${
                  isSelected("gender", gender)
                    ? "bg-blue-100 border-blue-300"
                    : "bg-white border-gray-300"
                }`}
                onClick={() => handleFilterChange("gender", gender)}
              >
                {gender}
              </button>
            ))}
          </div>
        </div>

        {/* Age */}
        <div className="mb-4">
          <h4 className="text-sm text-gray-500 mb-2">Age</h4>
          <div className="flex flex-wrap gap-2">
            {["Young Adult", "Adult", "Senior"].map((age) => (
              <button
                key={age}
                className={`px-3 py-1 rounded-md border ${
                  isSelected("age", age)
                    ? "bg-blue-100 border-blue-300"
                    : "bg-white border-gray-300"
                }`}
                onClick={() => handleFilterChange("age", age)}
              >
                {age}
              </button>
            ))}
          </div>
        </div>

        {/* Niche */}
        <div className="mb-4">
          <h4 className="text-sm text-gray-500 mb-2">Niche</h4>
          <div className="flex flex-wrap gap-2">
            {[
              "Health",
              "Fashion",
              "Apps & Digital",
              "Automotive",
              "Children & Family",
              "Gadgets",
              "Beauty",
              "Pets",
              "Home & Lifestyle",
              "Food & Beverages",
            ].map((niche) => (
              <button
                key={niche}
                className={`px-3 py-1 rounded-md border flex items-center ${
                  isSelected("niche", niche)
                    ? "bg-blue-100 border-blue-300"
                    : "bg-white border-gray-300"
                }`}
                onClick={() => handleFilterChange("niche", niche)}
              >
                <span className="material-icons text-sm mr-1">graphic_eq</span>
                {niche}
              </button>
            ))}
          </div>
        </div>

        {/* Situation */}
        <div className="mb-4">
          <h4 className="text-sm text-gray-500 mb-2">Situation</h4>
          <div className="flex flex-wrap gap-2">
            {[
              "Inside",
              "Outside",
              "Car",
              "Kitchen",
              "Office/Desk",
              "Sitting",
              "Standing",
              "Gym",
            ].map((situation) => (
              <button
                key={situation}
                className={`px-3 py-1 rounded-md border ${
                  isSelected("situation", situation)
                    ? "bg-blue-100 border-blue-300"
                    : "bg-white border-gray-300"
                }`}
                onClick={() => handleFilterChange("situation", situation)}
              >
                {situation}
              </button>
            ))}
          </div>
        </div>

        {/* Location */}
        <div className="mb-4">
          <h4 className="text-sm text-gray-500 mb-2">Location</h4>
          <div className="flex flex-wrap gap-2">
            {["Inside", "Outside", "Car", "Kitchen", "Office/Desk"].map(
              (location) => (
                <button
                  key={location}
                  className={`px-3 py-1 rounded-md border ${
                    isSelected("location", location)
                      ? "bg-blue-100 border-blue-300"
                      : "bg-white border-gray-300"
                  }`}
                  onClick={() => handleFilterChange("location", location)}
                >
                  {location}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
